import { useState, useEffect } from 'react';
import { X, Key, Shield, AlertTriangle, Save, Loader2, Bot, Camera, Database } from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import { CustomDropdown } from '../../components/CustomDropdown ';
import SingleSelectApiDropdown from '../../components/SingleSelectApiDropdown';

interface AddCredentialModalProps {
    open: boolean;
    onClose: () => void;
    assetId: number | null;
    clientId: any;
    isCamera?: boolean;
    assetType?: string;
    initialAgentId?: number | null;
}

type CredentialType = 'api' | 'snmp' | 'camera' | 'nvr';
type SnmpVersion = 'v1' | 'v2c' | 'v3';

const AddCredentialModal: React.FC<AddCredentialModalProps> = ({ open, onClose, assetId, clientId, isCamera = false, assetType = '', initialAgentId = null }) => {
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentCredentialId, setCurrentCredentialId] = useState<number | null>(null);
    const [agent, setAgent] = useState<any>(null);

    const initialFormState = {
        credential_name: '',
        description: '',
        credential_type: (isCamera ? 'camera' : 'snmp') as CredentialType,
        username: '',
        password: '',
        api_key: '',
        snmp_version: 'v2c' as SnmpVersion,
        snmp_community: 'public',
        snmp_username: '',
        snmp_auth_protocol: '',
        snmp_auth_key: '',
        snmp_priv_protocol: '',
        snmp_priv_key: '',
        port: 161,
        make: ''
    };

    const [formData, setFormData] = useState(initialFormState);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const fetchInitialAgent = async (id: number) => {
        try {
            const response = await baseapi.get(`/agents/${id}`);
            if (response.data.status) setAgent(response.data.data);
        } catch (error) { console.error("Error fetching agent:", error); }
    };

    const resetForm = () => {
        setFormData({ ...initialFormState, credential_type: isCamera ? 'camera' : 'snmp' });
        setErrors({}); setIsEditMode(false); setAgent(null); setCurrentCredentialId(null);
    };

    useEffect(() => {
        if (open) {
            setFormData(prev => ({ ...prev, credential_type: isCamera ? 'camera' : 'snmp' }));
            if (assetId) checkExistingCredentials();
            if (initialAgentId) fetchInitialAgent(initialAgentId);
        } else resetForm();
    }, [open, assetId, isCamera, initialAgentId]);

    const checkExistingCredentials = async () => {
        try {
            setFetching(true);
            const response = await baseapi.get(`/credentials`, { params: { query: JSON.stringify({ asset_id: assetId }) } });
            if (response.data.status && response.data.data?.length > 0) {
                const cred = response.data.data[0];
                let d: any = {};
                if (cred.credential_data) {
                    try { d = typeof cred.credential_data === 'string' ? JSON.parse(cred.credential_data) : cred.credential_data; }
                    catch (e) { console.error("Failed to parse data:", e); }
                }
                setFormData({
                    credential_name: cred.credential_name || '',
                    description: cred.description || '',
                    credential_type: (cred.credential_type as CredentialType) || (isCamera ? 'camera' : 'snmp'),
                    username: d.username || '',
                    password: d.password || d.api_secret || d.auth_password || '',
                    api_key: d.api_key || '',
                    snmp_version: d.snmp_version || 'v2c',
                    snmp_community: d.community || '',
                    snmp_username: d.snmp_username || '',
                    snmp_auth_protocol: d.auth_protocol || '',
                    snmp_auth_key: d.auth_password || '',
                    snmp_priv_protocol: d.priv_protocol || '',
                    snmp_priv_key: d.priv_password || '',
                    port: d.port || 161,
                    make: d.make || ''
                });
                if (cred.agent_id) fetchInitialAgent(cred.agent_id);
                setCurrentCredentialId(cred.id); setIsEditMode(true);
            } else resetForm();
        } catch (error) { console.error("Error fetching creds:", error); }
        finally { setFetching(false); }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const next: Record<string, string> = {};
        if (!formData.credential_name) next.credential_name = "Name is required";
        if (!agent) next.agent = "Agent is required";
        if (Object.keys(next).length > 0) return setErrors(next);

        try {
            setLoading(true);
            let credential_data = {};
            if (formData.credential_type === 'camera') credential_data = { username: formData.username, password: formData.password, make: formData.make };
            else if (formData.credential_type === 'nvr') credential_data = { username: formData.username, password: formData.password, make: formData.make, port: formData.port };
            else if (formData.credential_type === 'api') credential_data = { api_key: formData.api_key, api_secret: formData.password };
            else if (formData.credential_type === 'snmp') {
                if (formData.snmp_version === 'v3') credential_data = { snmp_version: 'v3', snmp_username: formData.snmp_username, auth_protocol: formData.snmp_auth_protocol, auth_password: formData.snmp_auth_key, priv_protocol: formData.snmp_priv_protocol, priv_password: formData.snmp_priv_key, make: formData.make };
                else credential_data = { snmp_version: formData.snmp_version, community: formData.snmp_community, make: formData.make };
            }
            const payload = { credential_name: formData.credential_name, credential_type: formData.credential_type, client_id: Number(agent.client_id), asset_id: assetId, agent_id: Number(agent.id), credential_data };
            const res = isEditMode ? await baseapi.put(`/credentials/${currentCredentialId}`, payload) : await baseapi.post('/credentials', payload);
            if (res.data.status) { showSuccess(`Credential ${isEditMode ? 'updated' : 'added'}`); resetForm(); onClose(); }
        } catch (error: any) { showError(error.message || "Error saving"); }
        finally { setLoading(false); }
    };

    if (!open) return null;

    return (
        <>
            <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] z-[9998]" onClick={onClose} />
            <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4">
                <div className="w-full max-w-xl bg-white border border-slate-200 shadow-2xl rounded-2xl text-slate-900 relative max-h-[90vh] overflow-y-auto custom-scroll animate-in zoom-in-95 duration-200">
                    <div className="sticky top-0 bg-white/80 backdrop-blur-md border-b border-slate-100 px-6 py-4 z-20 flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-[#422568] rounded-lg"><Key className="w-5 h-5 text-white" /></div>
                            <div>
                                <h2 className="text-sm font-black uppercase tracking-widest">{fetching ? 'Initialising...' : (isEditMode ? 'Edit Credential' : 'Configure Credential')}</h2>
                                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-tighter mt-1">Access & Authentication</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1.5 hover:bg-slate-50 rounded-full transition-all"><X className="w-5 h-5" /></button>
                    </div>

                    {fetching ? (
                        <div className="p-16 flex flex-col items-center justify-center text-slate-400">
                            <Loader2 className="w-8 h-8 animate-spin mb-3 text-[#422568]" />
                            <p className="text-[11px] font-black uppercase tracking-widest">Retrieving Secure Data</p>
                        </div>
                    ) : (
                        <div className="p-6 space-y-5">
                            <div>
                                <SingleSelectApiDropdown label="Select Probe Agent" value={agent} onChange={(item) => { setAgent(item); if (item) setErrors(p => ({ ...p, agent: '' })); }} apiEndpoint="/agents" icon={Bot} placeholder="Assign an agent for this credential..." displayProperty="name" searchFields={['name']} required={true} additionalQueries={{ client_id: clientId, agent_type: 'probe' }} searchInputPlaceholder="Search agents..." />
                                {errors.agent && <p className="text-rose-500 text-[10px] font-bold mt-1.5 flex items-center"><AlertTriangle className="w-3 h-3 mr-1" />{errors.agent}</p>}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5 block">Credential Name *</label>
                                    <input type="text" className={`w-full bg-slate-50 border ${errors.credential_name ? 'border-rose-500' : 'border-slate-200'} rounded-xl px-3 py-2 text-[11px] font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#422568]/10 focus:border-[#422568] transition-all`} value={formData.credential_name} onChange={(e) => setFormData({ ...formData, credential_name: e.target.value })} placeholder="e.g. SNMP-ReadOnly" />
                                </div>
                                 {!isCamera && (
                                     <div>
                                         <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5 block">Access Level</label>
                                         {(() => {
                                             const dropdownData = [
                                                 { label: 'SNMP', value: 'snmp' },
                                                 { label: 'API Integration', value: 'api' }
                                             ];
                                             if (assetType === 'nvr') {
                                                 dropdownData.push({ label: 'NVR', value: 'nvr' });
                                             }
                                             return (
                                                 <CustomDropdown data={dropdownData} selectedValue={formData.credential_type} onSelect={(v) => setFormData({ ...formData, credential_type: v as CredentialType })} width="w-full" buttonHeight={10} />
                                             );
                                         })()}
                                     </div>
                                 )}
                            </div>

                            <div className="p-5 bg-slate-50 border border-slate-100 rounded-xl space-y-4">
                                {formData.credential_type === 'camera' && (
                                     <>
                                         <div className="flex items-center gap-2 text-[#422568] text-[10px] font-black uppercase tracking-widest border-b border-slate-200 pb-2">
                                             <Camera className="w-4 h-4" /> Visual Identity Access
                                         </div>
                                         <div className="grid grid-cols-2 gap-3">
                                             <input placeholder="Auth Username" className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[11px] font-bold" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} />
                                             <input type="password" placeholder="Auth Password" className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[11px] font-bold" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} />
                                             <input placeholder="Brand Make (e.g. Hikvision)" className="col-span-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[11px] font-bold" value={formData.make} onChange={(e) => setFormData({ ...formData, make: e.target.value })} />
                                         </div>
                                     </>
                                 )}

                                 {formData.credential_type === 'nvr' && (
                                     <>
                                         <div className="flex items-center gap-2 text-[#422568] text-[10px] font-black uppercase tracking-widest border-b border-slate-200 pb-2">
                                             <Database className="w-4 h-4" /> NVR Authentication Settings
                                         </div>
                                         <div className="grid grid-cols-2 gap-3">
                                             <input placeholder="Auth Username" className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[11px] font-bold" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} />
                                             <input type="password" placeholder="Auth Password" className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[11px] font-bold" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} />
                                             <input type="number" placeholder="Port" className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[11px] font-bold" value={formData.port || ''} onChange={(e) => setFormData({ ...formData, port: Number(e.target.value) || 0 })} />
                                             <input placeholder="Brand Make (e.g. Hikvision)" className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[11px] font-bold" value={formData.make} onChange={(e) => setFormData({ ...formData, make: e.target.value })} />
                                         </div>
                                     </>
                                 )}

                                {formData.credential_type === 'snmp' && (
                                    <>
                                        <div className="flex items-center gap-2 text-[#422568] text-[10px] font-black uppercase tracking-widest border-b border-slate-200 pb-2"><Shield className="w-4 h-4" /> SNMP Settings</div>
                                        <div className="flex gap-2">
                                            {['v1', 'v2c', 'v3'].map(v => (
                                                <button key={v} type="button" onClick={() => setFormData({ ...formData, snmp_version: v as SnmpVersion })} className={`flex-1 py-1 text-[10px] font-black rounded-lg border transition-all ${formData.snmp_version === v ? 'bg-[#422568] border-[#422568] text-white shadow-lg shadow-[#422568]/20' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}>{v.toUpperCase()}</button>
                                            ))}
                                        </div>
                                        {formData.snmp_version !== 'v3' ? (
                                            <input placeholder="Community Payload (ReadOnly)" className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[11px] font-black" value={formData.snmp_community} onChange={(e) => setFormData({ ...formData, snmp_community: e.target.value })} />
                                        ) : (
                                            <div className="grid grid-cols-2 gap-2">
                                                <input placeholder="User" className="col-span-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[11px] font-black" value={formData.snmp_username} onChange={(e) => setFormData({ ...formData, snmp_username: e.target.value })} />
                                                <input placeholder="Auth Prot." className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[10px] font-black" value={formData.snmp_auth_protocol} onChange={(e) => setFormData({ ...formData, snmp_auth_protocol: e.target.value })} />
                                                <input type="password" placeholder="Auth Key" className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[10px] font-black" value={formData.snmp_auth_key} onChange={(e) => setFormData({ ...formData, snmp_auth_key: e.target.value })} />
                                                <input placeholder="Priv Prot." className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[10px] font-black" value={formData.snmp_priv_protocol} onChange={(e) => setFormData({ ...formData, snmp_priv_protocol: e.target.value })} />
                                                <input type="password" placeholder="Priv Key" className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[10px] font-black" value={formData.snmp_priv_key} onChange={(e) => setFormData({ ...formData, snmp_priv_key: e.target.value })} />
                                            </div>
                                        )}
                                        <input placeholder="Device Maker (e.g. Cisco/Juniper)" className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[11px] font-black" value={formData.make} onChange={(e) => setFormData({ ...formData, make: e.target.value })} />
                                    </>
                                )}
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                                <button type="button" onClick={onClose} className="px-4 py-2 text-[11px] font-black text-slate-500 hover:text-slate-700 uppercase tracking-widest">Cancel</button>
                                <button type="button" onClick={handleSubmit} disabled={loading} className="px-6 py-2 bg-[#422568] text-white rounded-xl text-[11px] font-black shadow-lg shadow-[#422568]/20 hover:bg-[#422568] transition-all active:scale-95 flex items-center gap-2 uppercase tracking-widest">
                                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {isEditMode ? 'Update' : 'Confirm'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default AddCredentialModal;
